import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import {
  Button,
  Select,
  Input,
  Modal,
  Spin,
} from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import { withRouter } from "react-router-dom"
import ReferralDashboardContainer from "container/ReferralDashboard"
import agentSearchContainer from "container/BrokerDashboard/agentSearchContainer"
import { REFERRAL_STATUSES } from "./Utils"

const STATUS_FILTER_ALL = ""

const statusFilterOptions = [...REFERRAL_STATUSES]

const { Search } = Input

const OuterWrap = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
`

const Wrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 20px;
`

const InputWrap = styled.div`
  max-width: 293px;
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  height: max-content;
`

const Label = styled.label`
  min-width: fit-content;
`

const AgentSearchHelp = styled.div`
  color: #777;
  font-size: 12px;
  margin-top: 8px;
`

const SearchControls = (props) => {
  const [filters, setFilters] = useState({ status: "pre_escrow" })
  const [searchFieldsKey, setSearchFieldsKey] = useState(0)
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false)
  const [selectedAgentValue, setSelectedAgentValue] = useState(undefined)
  const agentSearchDebounceRef = useRef(null)
  const pendingNewReferralRef = useRef(null)

  const {
    getReferrals,
    getReferralDetails,
    createReferral,
    createReferralResponse,
    toggleReferralDrawer,
    setReferralFilterQueries,
    fetchAgentSearch,
    agents,
  } = props

  const { isFetching: isCreatingReferral } = createReferralResponse || {}

  useEffect(() => () => {
    if (agentSearchDebounceRef.current) {
      window.clearTimeout(agentSearchDebounceRef.current)
    }
  }, [])

  useEffect(() => {
    if (!isAgentModalOpen || !pendingNewReferralRef.current) return
    if (createReferralResponse && createReferralResponse.isFetching) return
    const response = createReferralResponse && createReferralResponse.data
    const referralId = response && (
      response.referral_id
      || response.id
      || response.referral?.referral_id
      || response.referral?.id
    )
    if (!referralId) return

    const {
      selectedAgent,
      selectedValue,
    } = pendingNewReferralRef.current
    pendingNewReferralRef.current = null
    setIsAgentModalOpen(false)
    setSelectedAgentValue(undefined)
    toggleReferralDrawer({
      bool: true,
      data: {
        referral_id: referralId,
        primaryAgentId: selectedAgent.id,
        agent_id: selectedAgent.id,
        status: "pre_escrow",
        isNew: false,
        selectedReferringAgent: selectedAgent,
        referringAgentSelectValue: selectedValue,
      },
    })
    getReferralDetails({ referralId, agentId: selectedAgent.id })
  }, [createReferralResponse, getReferralDetails, isAgentModalOpen, toggleReferralDrawer])

  const fetchWithFilters = (updatedFilters) => {
    const payload = {
      skip: 0,
      limit: 25,
      sort_by: "created_at",
      sort_order: "desc",
    }

    if (updatedFilters.status) payload.status = updatedFilters.status
    if (updatedFilters.search) payload.search = updatedFilters.search

    getReferrals(payload)
  }

  const handleStatusChange = (value) => {
    const isAll = value === undefined || value === null || value === STATUS_FILTER_ALL
    const updated = { ...filters }
    if (isAll) {
      delete updated.status
    } else {
      updated.status = value
    }
    setFilters(updated)
    setReferralFilterQueries(updated)
    fetchWithFilters(updated)
  }

  const handleSearch = (rawValue) => {
    const searchTerm = typeof rawValue === "string" ? rawValue.trim() : ""
    const updated = { ...filters }
    delete updated.agentEmail
    delete updated.clientEmail
    if (searchTerm) {
      updated.search = searchTerm
    } else {
      delete updated.search
    }
    setFilters(updated)
    setReferralFilterQueries(updated)
    fetchWithFilters(updated)
  }

  const handleReset = () => {
    const cleared = { status: "pre_escrow" }
    setFilters(cleared)
    setReferralFilterQueries(cleared)
    setSearchFieldsKey((k) => k + 1)
    fetchWithFilters(cleared)
  }

  const handleNewReferral = () => {
    setSelectedAgentValue(undefined)
    setIsAgentModalOpen(true)
  }

  const scheduleAgentSearch = (searchValue) => {
    if (agentSearchDebounceRef.current) {
      window.clearTimeout(agentSearchDebounceRef.current)
    }
    agentSearchDebounceRef.current = window.setTimeout(() => {
      agentSearchDebounceRef.current = null
      const q = (searchValue || "").trim()
      if (!q || !fetchAgentSearch) return
      fetchAgentSearch({ search: q })
    }, 500)
  }

  const getSelectedAgent = (value = selectedAgentValue) => {
    if (!value || !agents?.data?.agents) return null
    const agentId = parseInt(String(value).split("::")[0], 10)
    if (Number.isNaN(agentId)) return null
    return agents.data.agents.find(agent => agent.id === agentId) || null
  }

  const handleCreateReferralForAgent = (value = selectedAgentValue) => {
    const selectedAgent = getSelectedAgent(value)
    if (!selectedAgent || !createReferral) return
    const selectedValue = value
    pendingNewReferralRef.current = {
      selectedAgent,
      selectedValue,
    }

    const payload = {
      agent_id: selectedAgent.id,
      draft: false,
      state: null,
      client_id: null,
      client_source: "other_agency",
      client_type: "referral",
      is_direct_contract: false,
    }

    createReferral({
      payload,
      skipAutoOpen: true,
    })
  }

  const handleAgentSelect = (value) => {
    setSelectedAgentValue(value)
    handleCreateReferralForAgent(value)
  }

  const handleCloseAgentModal = () => {
    if (isCreatingReferral) return
    pendingNewReferralRef.current = null
    setIsAgentModalOpen(false)
    setSelectedAgentValue(undefined)
  }

  const renderAgentModal = () => (
    <Modal
      title="Create New Referral"
      visible={isAgentModalOpen}
      onCancel={handleCloseAgentModal}
      footer={null}
      destroyOnClose
    >
      <Select
        showSearch
        defaultActiveFirstOption={false}
        showArrow={false}
        filterOption={false}
        placeholder="Search by agent name or email address"
        style={{ width: "100%" }}
        value={selectedAgentValue}
        onSearch={scheduleAgentSearch}
        onChange={handleAgentSelect}
        disabled={isCreatingReferral}
        notFoundContent={(agents?.isFetching || agents?.isFetchingMore) ? (
          <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        ) : null}
        options={
          (agents && agents.data && agents.data.agents)
            ? agents.data.agents.map(d => ({
              label: `${d.firstname} ${d.lastname} - ${d.email}`,
              value: `${d.id}::${d.firstname} ${d.lastname}`,
            }))
            : []
        }
      />
      <AgentSearchHelp>
        Select the Radius agent who should own this referral. The referral will be created immediately and this agent will fill the Referring Broker / Agent Information section.
      </AgentSearchHelp>
    </Modal>
  )

  return (
    <OuterWrap>
      <Wrap>
        <InputWrap>
          <Label htmlFor="Filter">Filter by Status</Label>
          <Select
            style={{ width: 160 }}
            onChange={handleStatusChange}
            value={filters.status}
            placeholder="All"
            options={statusFilterOptions}
            allowClear
          />
        </InputWrap>
        <InputWrap>
          <Search
            key={`referral-search-general-${searchFieldsKey}`}
            placeholder="Search"
            onSearch={handleSearch}
            style={{ width: 200, border: "1px solid #C7C7C7", borderRadius: "4px" }}
          />
        </InputWrap>
        <InputWrap>
          <Search
            key={`referral-search-agent-${searchFieldsKey}`}
            placeholder="Search by Agent Email"
            onSearch={handleSearch}
            style={{ width: 300, border: "1px solid #C7C7C7", borderRadius: "4px" }}
          />
        </InputWrap>
        <InputWrap>
          <Search
            key={`referral-search-client-${searchFieldsKey}`}
            placeholder="Search by Client Email"
            onSearch={handleSearch}
            style={{ width: 300, border: "1px solid #C7C7C7", borderRadius: "4px" }}
          />
        </InputWrap>
      </Wrap>
      <Wrap>
        <InputWrap>
          <Button
            danger
            onClick={handleReset}
          >
            Reset Filters
          </Button>
        </InputWrap>
        <InputWrap>
          <Button
            type="primary"
            onClick={handleNewReferral}
          >
            New Referral
          </Button>
        </InputWrap>
      </Wrap>
      {renderAgentModal()}
    </OuterWrap>
  )
}

export default withRouter(ReferralDashboardContainer(agentSearchContainer(SearchControls)))
